import { Params } from '@angular/router';
import { Subscription } from 'rxjs';

import { QueryParamsService } from '~core/services/query-params.service';
import { OptionalType } from '~shared/core/types/optional.type';
import { QueryParamSynchronizerField } from './query-param-synchronizer-field';

export class QueryParamSynchronizer<T extends object> {
    private queryParamsChangedSub: OptionalType<Subscription>;

    constructor(
        private fieldsToSync: QueryParamSynchronizerField<T>[],
        private onQueryParamsChanged: (data: Partial<T>) => void,
        private queryParamsService: QueryParamsService
    ) {}

    initialize(): void {
        // sync from query params (down)
        this.queryParamsChangedSub = this.queryParamsService.queryParamsChanged$.subscribe(params => {
            this.onQueryParamsChanged(this.deserialize(params));
        });
    }

    /**
     * sync to query params (up)
     */
    setData(data: T): void {
        const updatedParams = this.createQueryParamMap(data);
        // Replace the current URL without adding a new entry to the history
        this.queryParamsService.updateQueryParams(updatedParams, { replaceUrl: true });
    }

    stopSync(): void {
        this.queryParamsChangedSub?.unsubscribe();
        this.queryParamsChangedSub = undefined;
    }

    private serialize(data: T, field: QueryParamSynchronizerField<T>): string | undefined {
        const value = data[field.property];
        if (value === undefined) {
            return undefined;
        }
        if (field.customSerializer) {
            return field.customSerializer.serialize(value);
        }
        switch (field.type) {
            case 'string':
            case 'number':
                return String(value);
            case 'boolean':
                return value ? 'true' : 'false';
            case 'object':
                return JSON.stringify(value);
            default:
                throw new Error(`Unsupported field type: ${field.type}`);
        }
    }

    private deserialize(queryParams: Params): Partial<T> {
        return this.fieldsToSync.reduce((acc, field) => {
            const value = queryParams[field.queryParamName || (field.property as string)] as string | undefined;
            acc[field.property] = this.deserializeField(value, field);
            return acc;
        }, {} as Partial<T>);
    }

    private deserializeField(value: string | undefined, field: QueryParamSynchronizerField<T>): any {
        if (value === undefined) {
            return undefined;
        }
        if (field.customSerializer) {
            return field.customSerializer.deserialize(value);
        }
        switch (field.type) {
            case 'string':
                return value;
            case 'number': {
                const number = Number(value);
                return Number.isNaN(number) ? undefined : number;
            }
            case 'boolean':
                return value === 'true';
            case 'object':
                try {
                    return JSON.parse(value);
                } catch {
                    return undefined;
                }
            default:
                throw new Error(`Unsupported field type: ${field.type}`);
        }
    }

    private createQueryParamMap(data: T): Record<string, any> {
        return this.fieldsToSync.reduce((acc, field) => {
            if (data[field.property] !== undefined) {
                const value = this.serialize(data, field);
                // null so query param is removed
                // eslint-disable-next-line no-null/no-null
                acc[field.queryParamName || field.property] = !!value ? value : null;
            } else {
                // null so query param is removed
                // eslint-disable-next-line no-null/no-null
                acc[field.queryParamName || field.property] = null;
            }
            return acc;
        }, {} as any);
    }
}
